import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';
import {
  IxApplication,
  IxApplicationHeader,
  IxAvatar,
  IxContent,
  IxDropdownItem,
  IxMenu,
  IxMenuItem,
} from '@siemens/ix-react';
import {
  iconCalculator,
  iconAuditReport,
  iconLightDark,
  iconLogOut,
} from '@siemens/ix-icons/icons';
import { useAuth } from '../auth/AuthProvider.js';
import siemensLogo from '../assets/siemens-logo.svg';
import { useIsAdmin } from '../auth/useIsAdmin.js';
import { useTheme } from '../theme/useTheme.js';

export function AppLayout() {
  const { account, signOut } = useAuth();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const { schema, toggle } = useTheme();

  const currentPath = location.pathname;
  const displayName = account?.name ?? account?.username ?? '';
  const avatarLabel = displayName || 'user';
  // Uses first letters of up to the first two whitespace-separated words.
  const initials = displayName.split(/\s+/).map(p => p.charAt(0)).slice(0, 2).join('');

  // The Siemens header renders its title inside its own shadow DOM, and IX exposes
  // no theme variable for the font weight (--theme-app-header--* only covers colors
  // and border). So inject a small style sheet once into the shadow root to make the
  // title bold. The [data-app-header-style] guard prevents duplicates; useCallback
  // keeps the stable ref from being recreated every render.
  const headerRef = useCallback((el: HTMLElement | null) => {
    const root = el?.shadowRoot as ShadowRoot | undefined;
    if (!root) {return;}
    if (root.querySelector('[data-app-header-style]')) {return;}
    const style = document.createElement('style');
    style.dataset['appHeaderStyle'] = '';
    style.textContent = `
      .application-name { font-weight: 700 !important; }
    `;
    root.appendChild(style);
  }, []);

  return (
    <IxApplication>
      <IxApplicationHeader ref={headerRef} name="Transfer Pricing System">
        <img slot="logo" src={siemensLogo} alt="Siemens" />
        <IxAvatar
          aria-label={`Account menu for ${avatarLabel}`}
          username={displayName}
          initials={initials}
          tooltipText={displayName}
        >
          <IxDropdownItem
            icon={iconLightDark}
            label={schema === 'light' ? 'Dark mode' : 'Light mode'}
            onClick={toggle}
          />
          <IxDropdownItem
            icon={iconLogOut}
            label="Sign out"
            onClick={() => void signOut()}
          />
        </IxAvatar>
      </IxApplicationHeader>

      <IxMenu>

        <IxMenuItem
          icon={iconCalculator}
          active={currentPath === '/calculate'}
          onClick={() => void navigate('/calculate')}
        >
          Calculate
        </IxMenuItem>

        {isAdmin === true && (
          <IxMenuItem
            icon={iconAuditReport}
            active={currentPath === '/audit'}
            onClick={() => void navigate('/audit')}
          >
            Audit
          </IxMenuItem>
        )}
      </IxMenu>

      <IxContent>
        <Outlet />
      </IxContent>
    </IxApplication>
  );
}
